import { useRef, useState } from 'react';

import dayjs from 'dayjs';
import {
  Button,
  IconButton,
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  RadioGroup,
  RadioGroupItem,
  FieldLabel,
  toast
} from 'design-system';
import { ChevronDown, X } from 'lucide-react';

import { useUserConfig } from '@/features/_shared/user';
import type { ExtendedSeriesOptionsType } from '@/features/charts/types/highcharts';
import AbsoluteSelector from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector/AbsoluteSelector';
import { parseSelectedToIso } from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector/parseRelativeDateSelectionToIso';
import RelativeSelector from '@/features/markets/components/Chart/ForwardCurveChartBlock/CurveComparisonSelector/RelativeSelector';

import { getLatestTransactionDate } from './util/getLatestTransactionDate';

type ComparisonType = 'absolute' | 'relative';

const showDuplicatedErrorMessage = () => {
  toast.add({ title: 'This date is already selected!', type: 'error', timeout: 5000 });
};

const CurveComparisonSelector = ({
  chartSeries,
  label,
  transactionDates,
  comparisonSelectorIndex,
  deleteFromTemplateSeriesWith,
  populateTemplateSeriesWith
}: {
  chartSeries: ExtendedSeriesOptionsType[] | undefined;
  label: string;
  transactionDates: string[];
  comparisonSelectorIndex: number;
  deleteFromTemplateSeriesWith: (comparisonSelectorIndex: number) => void;
  populateTemplateSeriesWith: (selectedDateString: string, comparisonSelectorIndex: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [compareTo, setCompareTo] = useState('1');
  const [compareToUnit, setCompareToUnit] = useState('d');
  const latestTransactionDate = getLatestTransactionDate(chartSeries);
  const selectorNumber = comparisonSelectorIndex + 1;
  const relativeRadioRef = useRef<HTMLButtonElement>(null);

  const transactionDate =
    transactionDates.length > 0 ? transactionDates[comparisonSelectorIndex] : null;
  const [comparisonDate, setComparisonDate] = useState<Date | null>(
    transactionDate ? dayjs(transactionDate).toDate() : null
  );
  const [comparisonType, setComparisonType] = useState<ComparisonType>(
    comparisonDate ? 'absolute' : 'relative'
  );
  const { data: userConfigData } = useUserConfig();
  const dateFormat = (userConfigData?.dateFormat.value ?? 'dd-MMM-yy').toUpperCase();
  const relativeSelectionInvalid =
    !compareTo || compareTo.length < 1 || compareTo === '0' || compareTo === '00';
  const applyDisabled =
    comparisonType === 'relative' ? relativeSelectionInvalid : comparisonDate === null;

  const onAddComparison = () => {
    if (applyDisabled) return;

    const selectedDateString = parseSelectedToIso({
      startPoint: latestTransactionDate,
      comparisonDate,
      comparisonType,
      compareTo,
      compareToUnit
    });
    if (transactionDates.includes(selectedDateString)) {
      showDuplicatedErrorMessage();
      return;
    }

    populateTemplateSeriesWith(selectedDateString, comparisonSelectorIndex);
    setOpen(false);
  };

  return (
    <div className='flex items-center gap-1'>
      <Popover
        modal={false}
        open={open}
        onOpenChange={setOpen}
      >
        <PopoverTrigger
          render={
            <Button
              aria-label={
                transactionDate
                  ? `Edit date comparison ${selectorNumber}`
                  : `${label} ${selectorNumber}`
              }
              variant='outline'
            />
          }
        >
          {transactionDate
            ? dayjs(transactionDate).format(dateFormat)
            : `${label} ${selectorNumber}`}
          <ChevronDown data-icon='inline-end' />
        </PopoverTrigger>
        <PopoverContent
          align='start'
          aria-label='Date comparison'
          className='w-[min(26rem,calc(100vw-2rem))] gap-4 p-4'
          initialFocus={relativeRadioRef}
        >
          <PopoverHeader>
            <PopoverTitle>Date comparison</PopoverTitle>
            <PopoverDescription>Select a date to compare.</PopoverDescription>
          </PopoverHeader>
          <RadioGroup
            value={comparisonType}
            onValueChange={value => {
              if (value === 'relative' || value === 'absolute') {
                setComparisonType(value);
              }
            }}
          >
            <RelativeSelector
              compareTo={compareTo}
              compareToUnit={compareToUnit}
              radioRef={relativeRadioRef}
              setCompareTo={setCompareTo}
              setCompareToUnit={setCompareToUnit}
              setComparisonType={setComparisonType}
            />
            <div className='flex items-center gap-2'>
              <RadioGroupItem
                id={`comparison-type-absolute-${selectorNumber}`}
                value='absolute'
              />
              <FieldLabel htmlFor={`comparison-type-absolute-${selectorNumber}`}>
                Compare to date
              </FieldLabel>
            </div>
            <AbsoluteSelector
              comparisonDate={comparisonDate}
              comparisonType={comparisonType}
              setComparisonDate={setComparisonDate}
              startPoint={latestTransactionDate}
            />
          </RadioGroup>
          <div className='flex justify-end'>
            <Button
              aria-label='Apply comparison'
              disabled={applyDisabled}
              onClick={onAddComparison}
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      {transactionDate && (
        <IconButton
          label={`Remove date comparison ${selectorNumber}`}
          size='icon-sm'
          variant='ghost'
          onClick={() => {
            deleteFromTemplateSeriesWith(comparisonSelectorIndex);
          }}
        >
          <X />
        </IconButton>
      )}
    </div>
  );
};

export default CurveComparisonSelector;
